import { persistentAtom } from './persistence';
import { addReminder } from './reminders';
import type {
  AnchorSpec,
  IntervalSpec,
  ScheduledFrequency,
  ScheduledReminder,
} from '@/data/scheduledReminders';
import { nowWithOffsetMs } from '@/utils/clock';

const $scheduledReminders = persistentAtom<ScheduledReminder[]>(
  'scheduledReminders',
  [],
);

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function msFromInterval(every: IntervalSpec) {
  const minutes = every.minutes ?? 0;
  const hours = every.hours ?? 0;
  return (minutes * 60 + hours * 60 * 60) * 1000;
}

function anchorBase(anchor: AnchorSpec | undefined, now: number) {
  if (!anchor) return 0; // epoch
  if (anchor.type === 'epoch') return 0;
  if (anchor.type === 'timestamp') return anchor.at;
  const d = new Date(now);
  if (anchor.type === 'midnight') {
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  if (anchor.type === 'startOfHour') {
    d.setMinutes(0, 0, 0);
    return d.getTime();
  }
  return 0;
}

function occurrencesBetween(
  freq: ScheduledFrequency,
  from: number,
  to: number,
) {
  if (to <= from) return false;
  if (freq.kind === 'interval') {
    const ms = msFromInterval(freq.every);
    if (ms <= 0) return false;
    const base = anchorBase((freq as any).anchor, to);
    const lastSlot = Math.floor((from - base) / ms);
    const curSlot = Math.floor((to - base) / ms);
    return curSlot > lastSlot;
  }
  if (freq.kind === 'hourly') {
    const minutes = freq.minutes ?? [0];
    const start = new Date(from);
    const end = new Date(to);
    const hourStart = new Date(start);
    hourStart.setMinutes(0, 0, 0);
    while (hourStart.getTime() <= end.getTime()) {
      for (const m of minutes) {
        const t = new Date(hourStart);
        t.setMinutes(m, 0, 0);
        const ts = t.getTime();
        if (ts > from && ts <= to) return true;
      }
      hourStart.setHours(hourStart.getHours() + 1);
    }
    return false;
  }
  if (freq.kind === 'daily') {
    const times = freq.times ?? [{ hour: 0, minute: 0 }];
    const startDay = new Date(from);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(to);
    endDay.setHours(0, 0, 0, 0);
    const dayCursor = new Date(startDay);
    while (dayCursor.getTime() <= endDay.getTime()) {
      for (const tt of times) {
        const t = new Date(dayCursor);
        t.setHours(tt.hour ?? 0, tt.minute ?? 0, 0, 0);
        const ts = t.getTime();
        if (ts > from && ts <= to) return true;
      }
      dayCursor.setDate(dayCursor.getDate() + 1);
    }
    return false;
  }
  if (freq.kind === 'weekly') {
    const days = freq.days ?? [0];
    const times = freq.times ?? [{ hour: 0, minute: 0 }];
    const startDay = new Date(from);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(to);
    endDay.setHours(0, 0, 0, 0);
    const dayCursor = new Date(startDay);
    while (dayCursor.getTime() <= endDay.getTime()) {
      const weekday = dayCursor.getDay();
      if (days.includes(weekday)) {
        for (const tt of times) {
          const t = new Date(dayCursor);
          t.setHours(tt.hour ?? 0, tt.minute ?? 0, 0, 0);
          const ts = t.getTime();
          if (ts > from && ts <= to) return true;
        }
      }
      dayCursor.setDate(dayCursor.getDate() + 1);
    }
    return false;
  }
  return false;
}

let intervalHandle: ReturnType<typeof setInterval> | null = null;

function checkDue() {
  const now = nowWithOffsetMs();
  const list = $scheduledReminders.get();
  let updated = false;
  const newList = list.map(s => {
    if (!s.enabled) return s;
    const last = s.lastRun ?? 0;
    if (occurrencesBetween(s.frequency, last, now)) {
      const reminder = {
        id: genId(),
        ...s.template,
        description: s.template.description ?? '',
        time: now,
        tags: s.template.tags ?? [],
      } as const;
      // create the reminder
      addReminder(reminder);
      updated = true;
      return { ...s, lastRun: now };
    }
    return s;
  });
  if (updated) $scheduledReminders.set(newList);
}

export function startScheduledReminders() {
  if (!intervalHandle) {
    checkDue();
    intervalHandle = setInterval(checkDue, 60 * 1000);
  }
}

export function stopScheduledReminders() {
  if (intervalHandle) {
    clearInterval(intervalHandle);
    intervalHandle = null;
  }
}

export function addScheduledReminder(
  sr: Omit<ScheduledReminder, 'id' | 'lastRun'>,
) {
  // set lastRun to now to avoid immediate backfill runs; caller can override if desired
  const entry: ScheduledReminder = {
    ...sr,
    id: genId(),
    lastRun: nowWithOffsetMs(),
  };
  $scheduledReminders.set([...$scheduledReminders.get(), entry]);
  return entry.id;
}

export function removeScheduledReminder(id: string) {
  $scheduledReminders.set($scheduledReminders.get().filter(s => s.id !== id));
}

export { $scheduledReminders };

// start scheduler on module load
startScheduledReminders();
